import {
    Button,
    Checkbox,
    Chip,
    Collapse,
    FormControlLabel,
    CircularProgress,
    TableCell,
    TableRow,
    Typography,
    makeStyles,
    Box,
    Grid,
    TextField,
} from '@material-ui/core';
import {FC, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {AgeniumUser, InternalUser, AgentData, InternalUserRole} from '@amplify/athena-models';
import {allRoles, roleToNameMap} from './constants';

const useStyles = makeStyles({
    row: {
        cursor: 'pointer',
        '& .MuiTableCell-root': {
            fontWeight: (props: {isOpen: boolean}) => (props.isOpen ? 'bold' : 'normal'),
        },
    },
});

interface OneContactProps {
    internalUser: InternalUser;
    isDeleteLoading: boolean;
    isUpdateLoading: boolean;
    onDeleteClick: (email: string) => void;
    onUpdateClick: (user: InternalUser) => void;
}

function getDefaultAgeniumUser(): AgeniumUser {
    return {
        keyedContractNumber: '',
        agentAssisted: true,
        agentAssistedContractNumber: '',
        // TODO get from google? are these required?
        agentKeyedFirstName: '',
        agentKeyedLastName: '',
    };
}
function getDefaultAgentUserData(): AgentData {
    return {
        hubspotId: '',
        managerName: '',
        managerLicenseNumber: '',
    };
}
export const OneInternalUser: FC<OneContactProps> = observer(
    ({internalUser, isDeleteLoading, isUpdateLoading, onDeleteClick, onUpdateClick}) => {
        const [open, setOpen] = useState(false);
        const classes = useStyles({isOpen: open});
        const [roles, setRoles] = useState<InternalUserRole[]>([]);
        const [ageniumUserData, setAgeniumUserData] = useState(internalUser.ageniumUserData);
        const [agentUserData, setAgentUserData] = useState(internalUser.agentData);

        useEffect(() => {
            setRoles(internalUser.roles || []);
        }, [internalUser]);

        function handleCheck(role: InternalUserRole, isChecked: boolean) {
            const newRoles = isChecked ? [...roles, role] : roles.filter((r) => r !== role);
            if (role === InternalUserRole.AGENIUM_AGENT) {
                if (isChecked && !ageniumUserData) {
                    setAgeniumUserData(getDefaultAgeniumUser());
                } else {
                    setAgeniumUserData(null);
                }
            }
            setRoles(newRoles);
        }

        function handleSave() {
            console.log({...internalUser, roles, ageniumUserData, agentData: agentUserData});
            onUpdateClick({...internalUser, roles, ageniumUserData, agentData: agentUserData});
        }

        return (
            <>
                <TableRow onClick={() => setOpen(!open)} classes={{root: classes.row}} hover>
                    <TableCell>{internalUser.email}</TableCell>
                    <TableCell>{roles.map((r) => roleToNameMap.get(r) || r).join(',')}</TableCell>
                    <TableCell style={{width: '160px'}}>
                        <Chip label={open ? 'Close' : '...'} onClick={() => setOpen(!open)} clickable color="primary" />
                    </TableCell>
                </TableRow>

                <TableRow>
                    <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={7}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box
                                margin={1}
                                padding={1}
                                style={{display: 'flex', flexDirection: 'column', rowGap: '20px'}}
                            >
                                <div>
                                    {allRoles.map((role) => (
                                        <FormControlLabel
                                            key={role.role}
                                            control={
                                                <Checkbox
                                                    checked={roles.includes(role.role)}
                                                    onChange={(e) => handleCheck(role.role, e.target.checked)}
                                                    color="primary"
                                                />
                                            }
                                            label={role.displayName}
                                        />
                                    ))}
                                </div>
                                {roles.includes(InternalUserRole.AGENT) && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={4}>
                                            <TextField
                                                variant="outlined"
                                                label="Manager Name"
                                                value={agentUserData?.managerName || ''}
                                                onChange={(e) =>
                                                    setAgentUserData({
                                                        ...(agentUserData || getDefaultAgentUserData()),
                                                        managerName: e.target.value,
                                                    })
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                variant="outlined"
                                                label="Manager License Number"
                                                value={agentUserData?.managerLicenseNumber || ''}
                                                onChange={(e) =>
                                                    setAgentUserData({
                                                        ...(agentUserData || getDefaultAgentUserData()),
                                                        managerLicenseNumber: e.target.value,
                                                    })
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField
                                                variant="outlined"
                                                label="Hubspot ID"
                                                value={agentUserData?.hubspotId || ''}
                                                onChange={(e) =>
                                                    setAgentUserData({
                                                        ...(agentUserData || getDefaultAgentUserData()),
                                                        hubspotId: e.target.value,
                                                    })
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                    </Grid>
                                )}
                                {roles.includes(InternalUserRole.AGENIUM_AGENT) && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <TextField
                                                variant="outlined"
                                                label="Keyed Contract Number"
                                                value={ageniumUserData?.keyedContractNumber || ''}
                                                onChange={(e) =>
                                                    setAgeniumUserData({
                                                        ...(ageniumUserData || getDefaultAgeniumUser()),
                                                        keyedContractNumber: e.target.value,
                                                    })
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Agent Assisted Contract Number"
                                                variant="outlined"
                                                value={ageniumUserData?.agentAssistedContractNumber || ''}
                                                onChange={(e) =>
                                                    setAgeniumUserData({
                                                        ...(ageniumUserData || getDefaultAgeniumUser()),
                                                        agentAssistedContractNumber: e.target.value,
                                                    })
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Agent Keyed First Name"
                                                variant="outlined"
                                                value={ageniumUserData?.agentKeyedFirstName || ''}
                                                onChange={(e) =>
                                                    setAgeniumUserData({
                                                        ...(ageniumUserData || getDefaultAgeniumUser()),
                                                        agentKeyedFirstName: e.target.value,
                                                    })
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                label="Agent Keyed Last Name"
                                                variant="outlined"
                                                value={ageniumUserData?.agentKeyedLastName || ''}
                                                onChange={(e) =>
                                                    setAgeniumUserData({
                                                        ...(ageniumUserData || getDefaultAgeniumUser()),
                                                        agentKeyedLastName: e.target.value,
                                                    })
                                                }
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography>Is Agent Assisted</Typography>
                                            <Checkbox
                                                checked={ageniumUserData?.agentAssisted ?? true}
                                                onChange={(e) =>
                                                    setAgeniumUserData({
                                                        ...(ageniumUserData || getDefaultAgeniumUser()),
                                                        agentAssisted: e.target.checked,
                                                    })
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                )}

                                <Box style={{display: 'flex', justifyContent: 'space-between', columnGap: '20px'}}>
                                    <Button
                                        color="primary"
                                        onClick={handleSave}
                                        startIcon={
                                            isUpdateLoading ? <CircularProgress color="inherit" size={20} /> : null
                                        }
                                        disabled={isDeleteLoading || isUpdateLoading}
                                    >
                                        {isUpdateLoading ? 'Updating...' : 'Update'}
                                    </Button>
                                    <Button
                                        color="secondary"
                                        startIcon={
                                            isDeleteLoading ? <CircularProgress color="inherit" size={20} /> : null
                                        }
                                        onClick={() => onDeleteClick(internalUser.email)}
                                        disabled={isDeleteLoading || isUpdateLoading}
                                    >
                                        {isDeleteLoading ? 'Deleting...' : 'Delete'}
                                    </Button>
                                </Box>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </>
        );
    },
);
